export interface SymbolConfigResponse {
  symbols: string[];
}

interface ApiEnvelopeError {
  code: string;
  message: string;
  details?: unknown;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: ApiEnvelopeError;
  meta?: Record<string, unknown>;
}

export interface TradingStateResponse {
  symbol?: string;
  status?: string;
  lastUpdated?: number;
  latestPrice?: number;
  priceUpdatedAt?: number;
}

const rawBase = import.meta.env.VITE_API_BASE ?? '';
// Auth Token Management
let authToken: string | null = localStorage.getItem('agentk_auth');

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('agentk_auth', token);
  } else {
    localStorage.removeItem('agentk_auth');
  }
}

export const API_BASE = rawBase.endsWith('/') ? rawBase.slice(0, -1) : rawBase;

export function buildUrl(path: string): string {
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

function buildAuthHeaders(accept: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Accept': accept,
  };
  if (authToken) {
    headers['Authorization'] = authToken;
  }
  return headers;
}

async function handleJson<T>(path: string): Promise<T> {
  return handleJsonRequest<T>(path, {
    method: 'GET',
  });
}

export async function handleJsonRequest<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(buildUrl(path), {
    ...init,
    headers: {
      ...buildAuthHeaders('application/json'),
      ...(init.headers || {}),
    },
  });

  if (res.status === 401) {
    // Auth failed, clear token and maybe reload/redirect
    setAuthToken(null);
    window.dispatchEvent(new Event('auth:unauthorized'));
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Request failed (${res.status}): ${body}`);
  }

  const payload = await res.json() as unknown;
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'success' in payload &&
    typeof (payload as { success?: unknown }).success === 'boolean'
  ) {
    const envelope = payload as ApiEnvelope<T>;
    if (!envelope.success) {
      throw new Error(envelope.error?.message || 'Request failed');
    }
    return envelope.data as T;
  }

  return payload as T;
}

export async function fetchSymbols() {
  return handleJson<SymbolConfigResponse>('/api/v1/config');
}

export async function fetchState(symbol: string) {
  return handleJson<TradingStateResponse>(`/api/v1/status/${symbol}`);
}

export interface TradesResponse {
  stats: {
    totalPnl: string;
    totalFees: string;
    netPnl: string;
    winCount: number;
    lossCount: number;
    totalTrades: number;
    winRate: string;
    profitFactor?: string;
    maxDrawdown?: string;
    maxDrawdownPct?: string;
    sharpeRatio?: string;
    longestLosingStreak?: number;
    currentLosingStreak?: number;
    maxSingleWin?: string;
    maxSingleLoss?: string;
    averageWin?: string;
    averageLoss?: string;
    expectancy?: string;
    maxSingleWinTime?: number | null;
    maxSingleWinCoin?: string | null;
    maxSingleWinSide?: string | null;
    maxSingleLossTime?: number | null;
    maxSingleLossCoin?: string | null;
    maxSingleLossSide?: string | null;
  };
  entryLabelBreakdown?: Array<{
    label: string;
    rawLabel?: string;
    trades: number;
    wins: number;
    losses: number;
    winRate: string;
    grossPnl: string;
    fees: string;
    netPnl: string;
  }>;
  monthlyBreakdown?: Array<{
    period: string;
    grossPnl: string;
    fees: string;
    netPnl: string;
    trades: number;
    wins: number;
    losses: number;
    winRate: string;
  }>;
  yearlyBreakdown?: Array<{
    period: string;
    grossPnl: string;
    fees: string;
    netPnl: string;
    trades: number;
    wins: number;
    losses: number;
    winRate: string;
  }>;
  equityCurve?: Array<{
    time: number;
    equity: number;
    drawdown: number;
    drawdownPct: number;
  }>;
  fills: Array<{
    coin: string;
    side: string;
    price: string;
    size: number;
    pnl: string;
    fee: string;
    netPnl: string;
    time: number;
    provider?: string;
    entryLabel?: string;
    rawEntryLabel?: string;
  }>;
  notice?: string;
}

export async function fetchTrades(limit = 50, options?: { symbol?: string; provider?: 'ALL' | 'HYPERLIQUID' }) {
  const search = new URLSearchParams({ limit: String(limit) });
  if (options?.symbol) search.set('symbol', options.symbol);
  if (options?.provider) search.set('provider', options.provider);
  return handleJson<TradesResponse>(`/api/v1/trades?${search.toString()}`);
}

export interface LaneInfo {
  id: string;
  schedule: string;
  mode?: 'PAPER' | 'LIVE';
  symbols?: string[];
  perSymbol?: Record<string, {
    position: { direction: 'LONG' | 'SHORT'; entryPrice: number; stopPrice: number; size: number } | null;
    lastEvaluatedDay: number | null;
  }>;
}

export interface LanesResponse { lanes: LaneInfo[] }

export async function fetchLanes() {
  return handleJson<LanesResponse>('/api/v1/lanes');
}
